import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, RouterEvent } from '@angular/router';
import { IAppDataStore, LogService } from '@pershing/pershing-core';
import { MessagingConstants } from '../utils/messaging-constants';

export interface PershingOverlayConfig {
    renderOnce: boolean;        // Will only render the overlay once
    timeout: number;            // Timeout  (in seconds)
    allowedPages: string[];     // Only displays overlay for certian pageId's
}

const PershingOverlayDefaultConfig: PershingOverlayConfig = {
    renderOnce: false,
    timeout: 25,
    allowedPages: []
};

@Component({
    selector: 'pershing-overlay',
    templateUrl: './pershing-overlay.component.html'
})

export class PershingOverlayComponent implements OnDestroy, OnInit {

    // Configurable Properties
    private allowedPages: any[];   // Routes to overlay
    private renderOnce: boolean = false;
    private timeout: number = 30;

    // Assigned at Runtime
    private canAnimate: boolean = true;
    isLoading: boolean = false;
    private timerTriggered: boolean = false;
    private routerSub;
    private loadingStateMap = {};

    constructor(private appData: IAppDataStore, private router: Router, private logService: LogService) {}

    ngOnInit() {
        this.initializeConfiguration();
        this.initializeStateMap();
        this.routerSub = this.router.events.subscribe((event: RouterEvent) => {
            if (event && event.url) {
                let routeParams: any[] = event.url.split('/');
                for (let param of routeParams) {
                    // Only listen to route if allowedPages are defined
                    if (this.allowedPages && this.allowedPages.length > 0 && this.isAllowedPage(param)) {
                        this.setPageLoadingState(param, true);
                    }
                }
            }
        });
    }

    initializeConfiguration() {
        let customOverlayConfiguration = this.appData.get(MessagingConstants.PERSHING_OVERLAY_CONFIG);
        let config = customOverlayConfiguration || PershingOverlayDefaultConfig;
        this.renderOnce = config.renderOnce;
        this.allowedPages = config.allowedPages;
        this.timeout = config.timeout;
    }

    initializeStateMap() {
        this.loadingStateMap = [];
        for (let page of this.allowedPages) {
            this.loadingStateMap[page] = false;
        }
    }

    setPageLoadingState(page: string, display: boolean) {
        if (this.isAllowedPage(page)) {
            this.loadingStateMap[page] = display;
            let pageStates = Object.keys(this.loadingStateMap).map(key => this.loadingStateMap[key]);
            if (pageStates.every((val, i, arr) => val === arr[0])) {
                this.setDisplay(pageStates[0]);    // XNOR condition to trigger display change only if entire state map is truthy or falsey
            }
        }
    }

    @HostListener('document:pershing.overlay.Loading', ['$event'])
    initLoadingEvent(event: any) {
        if (event.detail && event.detail.component) {
            this.setPageLoadingState(event.detail.component, true);
        }
        this.logService.info('Recieved Loading Event');
    }

    @HostListener('document:pershing.overlay.LoadingComplete', ['$event'])
    completeLoadingEvent(event: any) {
        if (event.detail && event.detail.component) {
            this.setPageLoadingState(event.detail.component, false);
        }
        this.logService.info('Recieved Loading Complete');
    }

    isAllowedPage(route: string) {
        let isAllowed = false;
        if (route) {
            if (this.allowedPages && this.allowedPages.length > 0) {
                isAllowed = this.allowedPages.indexOf(route) > -1;
            } else {
                isAllowed =  true;
            }
        }
        return isAllowed;
    }

    setDisplay (visible) {
        if (visible) {
            if (this.renderOnce && this.canAnimate) {
                this.canAnimate = false;
                this.isLoading = true;
                this.overlayTimer();

            } else if (!this.renderOnce) {
                this.isLoading = true;
                this.overlayTimer();
            }
        } else {
            this.isLoading = false;
        }
    }

    overlayTimer() {
        if (!this.timerTriggered) {
            this.timerTriggered = true;
            setTimeout(this.clearOverlayState.bind(this), this.timeout * 1000);
        }
    }

    clearOverlayState() {
        this.initializeStateMap();
        this.setDisplay(false);
        this.timerTriggered = false;
    }

    ngOnDestroy() {
        if (this.routerSub) {
            this.routerSub.unsubscribe();
        }
    }

}
